"use client";

import { Monitor, Globe, Smartphone } from "lucide-react";
import Link from "next/link";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

const platforms = [
  {
    key: "desktop" as const,
    icon: Monitor,
    color: "leaf",
    recommended: true,
    specs: [
      {
        labelKey: "comparison.specs.fileSizes",
        valueKey: "comparison.desktop.fileSizes",
        highlight: true,
      },
      {
        labelKey: "comparison.specs.processing",
        valueKey: "comparison.desktop.processing",
      },
      {
        labelKey: "comparison.specs.storage",
        valueKey: "comparison.desktop.storage",
      },
      {
        labelKey: "comparison.specs.speed",
        valueKey: "comparison.desktop.speed",
        highlight: true,
      },
    ],
    ctaHref: "/download",
    ctaVariant: "primary" as const,
  },
  {
    key: "web" as const,
    icon: Globe,
    color: "leaf",
    recommended: false,
    specs: [
      {
        labelKey: "comparison.specs.fileSizes",
        valueKey: "comparison.web.fileSizes",
      },
      {
        labelKey: "comparison.specs.processing",
        valueKey: "comparison.web.processing",
      },
      {
        labelKey: "comparison.specs.storage",
        valueKey: "comparison.web.storage",
      },
      {
        labelKey: "comparison.specs.install",
        valueKey: "comparison.web.install",
        highlight: true,
      },
    ],
    ctaHref: "/login",
    ctaVariant: "outline" as const,
  },
  {
    key: "mobile" as const,
    icon: Smartphone,
    color: "leaf",
    recommended: false,
    specs: [
      {
        labelKey: "comparison.specs.fileSizes",
        valueKey: "comparison.mobile.fileSizes",
      },
      {
        labelKey: "comparison.specs.processing",
        valueKey: "comparison.mobile.processing",
      },
      {
        labelKey: "comparison.specs.storage",
        valueKey: "comparison.mobile.storage",
      },
      {
        labelKey: "comparison.specs.install",
        valueKey: "comparison.mobile.install",
        highlight: false,
      },
    ],
    ctaHref: "/download",
    ctaVariant: "outline" as const,
  },
];

const iconColorMap: Record<string, string> = {
  leaf: "bg-surface-success border-surface-success-border text-leaf-700 dark:text-leaf-300",
};

const highlightColorMap: Record<string, string> = {
  leaf: "text-leaf-600 dark:text-leaf-400",
};

export function PlatformComparison() {
  const { t } = useJsonTranslation("Platform");

  return (
    <div data-home-block="platform-comparison" className="relative">
      <div className="mx-auto max-w-4xl">
        <div className="mb-12 text-center">
          <div className="mb-6 inline-flex items-center gap-2">
            <div className="border-border/50 inline-flex items-center gap-2 rounded-full border bg-white/[0.04] px-4 py-2">
              <span className="text-muted-foreground text-sm">
                {t("comparison.badge")}
              </span>
            </div>
          </div>

          <h2 className="font-home-display text-foreground text-3xl font-semibold tracking-normal text-balance md:text-4xl lg:text-5xl">
            {t("comparison.title")}
          </h2>
          <p className="text-muted-foreground/70 mx-auto mt-5 max-w-2xl text-lg text-pretty">
            {t("comparison.subtitle")}
          </p>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {platforms.map((platform) => (
          <div
            key={platform.key}
            className={`uni-card group relative ${platform.recommended ? "ring-leaf-500/30 ring-1" : ""}`}
          >
            {platform.recommended && (
              <div className="bg-leaf-500 absolute top-0 right-0 rounded-tr-[calc(1rem-1px)] rounded-bl-lg px-3 py-1 text-[10px] font-semibold tracking-wide text-white uppercase">
                {t("comparison.recommended")}
              </div>
            )}

            <div
              className={`h-12 w-12 rounded-xl ${iconColorMap[platform.color]} mb-5 flex items-center justify-center border transition-transform duration-300 group-hover:scale-110`}
            >
              <platform.icon className="h-6 w-6" />
            </div>

            <h3 className="font-display text-foreground mb-1 text-xl font-semibold">
              {t(`comparison.${platform.key}.tagline`)}
            </h3>
            <p className="text-muted-foreground mb-5 text-sm leading-relaxed">
              {t(`comparison.${platform.key}.pitch`)}
            </p>

            <div className="border-border/30 mb-5 divide-y divide-[var(--color-border)]/30">
              {platform.specs.map((spec, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-2.5 text-sm"
                >
                  <span className="text-muted-foreground">
                    {t(spec.labelKey)}
                  </span>
                  <span
                    className={`font-medium ${spec.highlight ? highlightColorMap[platform.color] : "text-foreground"}`}
                  >
                    {t(spec.valueKey)}
                  </span>
                </div>
              ))}
            </div>

            {platform.ctaVariant === "primary" ? (
              <Link
                href={platform.ctaHref}
                className="bg-leaf-500 hover:bg-leaf-600 block w-full rounded-lg px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors"
              >
                {t(`comparison.${platform.key}.cta`)}
              </Link>
            ) : (
              <Link
                href={platform.ctaHref}
                className="border-border/50 text-muted-foreground hover:text-foreground hover:border-border block w-full rounded-lg border px-4 py-2.5 text-center text-sm font-semibold transition-colors"
              >
                {t(`comparison.${platform.key}.cta`)}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
